import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface WebhookAlert {
  id: string;
  admin_user_id: string;
  webhook_event_id: string;
  alert_type: 'in_app' | 'email';
  severity: 'critical' | 'warning' | 'info';
  read_status: boolean;
  notified_at: string;
  created_at: string;
}

export const useWebhookAlerts = () => {
  const queryClient = useQueryClient();

  const { data: alerts, isLoading } = useQuery({
    queryKey: ["webhook-alerts"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("admin_webhook_alerts")
        .select("*, stripe_webhook_events(*)")
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      return data || [];
    },
  });

  const markAsRead = useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await (supabase as any)
        .from("admin_webhook_alerts")
        .update({ read_status: true })
        .eq("id", alertId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-alerts"] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await (supabase as any)
        .from("admin_webhook_alerts")
        .update({ read_status: true })
        .eq("admin_user_id", user.id)
        .eq("read_status", false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-alerts"] });
      toast.success("All alerts marked as read");
    },
  });

  const unreadCount = alerts?.filter((alert: WebhookAlert) => !alert.read_status).length || 0;

  return {
    alerts,
    isLoading,
    unreadCount,
    markAsRead,
    markAllAsRead,
  };
};
